import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import useSse from "./hooks/use-sse";
import TemplateDefault from "../../components/template-default";
import Card from "../../components/card";
import {AgGridReact} from "ag-grid-react";
import {LocalizationAgGrid} from "../../utils/localization-ag-grid";
import statusCellRenderer from "./components/status-cell-renderer";
import {useParams} from "react-router-dom";
import useFetchData from "./hooks/use-fetch-data";
import CardTitle from "./components/card-title";
import LaneModal from "./components/lane-modal";
import TruncateCellRenderer from "../../components/cell-renderer/truncate-cell-renderer";
import {ProjectContext} from "../../context/project-context";
import productService from "../../services/product-service";
import general from "../../utils/general";

const BASENAME = process.env.PUBLIC_URL;

const Cameras = () => {
    const {ibge} = useParams();

    const {user} = useContext(ProjectContext);

    const [code, setCode] = useState(ibge);
    const [open, setOpen] = useState(false);
    const [navigateActive, setNavigateActive] = useState(false);
    const [cameraSelected, setCameraSelected] = useState({});

    const {hash, project} = useFetchData(code);

    const interval = useRef(undefined);
    const next = useRef(project?.next);

    const {noc, lastUpdate} = useSse(hash, code);

    useEffect(() => {
        document.title = "SISTEMA INTERNO";
        const navigateValue = localStorage.getItem("navigate");
        if (navigateValue && navigateValue === "true") {
            setNavigateActive(true);
            handleNavigate(true);
        }

        const orderColumns = JSON.parse(localStorage.getItem("cameras-order-columns"));
        if (orderColumns) general.sortColumns(columns, orderColumns);

        const sortColumns = JSON.parse(localStorage.getItem("cameras-sort-columns"));
        if (sortColumns) {
            columns.forEach(c => {
                const columnFind = sortColumns.find(sc => sc.field === c.field);
                if (columnFind) c.initialSort = columnFind.sort;
            })
        }

        setColumnDefs(columns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        next.current = project?.next;
    }, [project]);

    const handleNavigate = (checked) => {
        setNavigateActive(checked);
        localStorage.setItem("navigate", checked);

        clearInterval(interval.current);

        if (checked) {
            interval.current = setInterval(() => setCode(next.current), 10000);
        }
    }

    const gridRef = useRef();

    const [columnDefs, setColumnDefs] = useState([]);

    const columns = [
        { field: "status",
            headerName: "",
            sortable: true,
            width: 50,
            comparator: (valueA, valueB) => {
                const aInt = valueA === 'ACTIVE' ? 1 : (valueA === 'WARNING' ? 2 : 3);
                const bInt = valueB === 'ACTIVE' ? 1 : (valueB === 'WARNING' ? 2 : 3);

                return aInt - bInt;
            },
            cellRenderer: statusCellRenderer,
            cellClass: "justify-center",
            sort: "desc"
        },
        { field: "capturePlaceName", headerName: "Ponto de coleta", cellRenderer: TruncateCellRenderer },
        { field: "ipCapturePlace", headerName: "IP" },
        { field: "pending", headerName: "Pendentes", valueFormatter: ({value}) => value ?? 0, width: 100 },
        { field: "lanes", headerName: "Faixas", valueFormatter: ({value}) => value?.length ?? "", width: 70 },
        { field: "description", headerName: "Observações", cellRenderer: TruncateCellRenderer, flex: 1, minWidth: 250 },
        {
            field: "hardware-attendance",
            headerName: "Chamado",
            hide: !user.permissions.includes("attendances"),
            cellRenderer: () => <span className="text-blue-700 w-full hover:underline">Novo</span>,
            width: 100
        },
    ];

    const rowClassRules = useMemo(() => {
        return {
            "error": ({data: {status}}) => status === "ERROR",
            "warning": ({data: {status}}) => status === "WARNING",
            "success": ({data: {status}}) => status === "ACTIVE",
        };
    }, []);

    const onCellClicked = ({data, column}) => {
        if (column.colId === "hardware-attendance") {
            productService.insertOrUpdatePcl(code, data.idCapturePlace).finally(() => {
                window.open(`${BASENAME}/hardware-attendances/form?ibge=${code}&pcl=${data.idCapturePlace}`, `chamado-${data.idCapturePlace}-${code}`);
            });

            return;
        }

        setOpen(true);
        setCameraSelected(data);
    }

    const onColumnMoved = (params) => {
        const columns = params.api.getAllDisplayedColumns().map(col => col.colId);
        localStorage.setItem(`cameras-order-columns`, JSON.stringify(columns))
    }

    const onSortChanged = (params) => {
        const sort = params.columns
            .filter(c => c.getSort())
            .map(c => ({field: c.colDef.field, sort: c.getSort()}));

        if (sort.length > 0) {
            localStorage.setItem(`cameras-sort-columns`, JSON.stringify(sort))
        } else {
            localStorage.removeItem("cameras-sort-columns");
        }
    }

    return (
       <TemplateDefault title="Câmeras">
           <Card title={<CardTitle project={project} lastUpdate={lastUpdate} checked={navigateActive} onChange={handleNavigate}/>}>
               <div className={"ag-theme-quartz grid"}>
                   <AgGridReact
                       ref={gridRef}
                       rowHeight={32}
                       rowData={noc}
                       columnDefs={columnDefs}
                       animateRows={false}
                       suppressRowClickSelection={true}
                       suppressCellFocus={true}
                       gridOptions={{localeText: LocalizationAgGrid}}
                       onCellClicked={onCellClicked}
                       rowClassRules={rowClassRules}
                       domLayout='autoHeight'
                       onColumnMoved={onColumnMoved}
                       onSortChanged={onSortChanged}
                   />
               </div>
           </Card>

           <LaneModal set={setOpen} open={open} data={cameraSelected}/>
       </TemplateDefault>
    );
};

export default Cameras;
